import { FormDialogMode, useNotify } from '@globaleris/ui.library'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoPeaje } from '../../../../api'
import { PriceComponentInstancesDataTableModel } from '../../../../api/Argentia/endpoints/priceComponents'
import { EnumPriceComponentReference } from '../../../../api/Argentia/enums'
import { EditablePriceModel } from '../../../../api/Argentia/model/editablePriceModel'
import { EnumPresionTension } from '../../../../api/oData/enums/enumPresionTension'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import Page, { PageLayout } from '../../../../components/page/page'
import PageCenterColumn from '../../../../components/page/pageCenterColumn'
import PageHeader from '../../../../components/page/pageHeader'
import PageLeftColumn from '../../../../components/page/pageLeftColumn'
import { deletePriceComponent } from '../../../../components/shared/priceComponentsTable/priceComponentsTable.service'
import { enumEnumToValueNumber, enumEnumToValueString } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { getActiveCompany } from '../../../../redux'
import { mapEditableModelPriceFields } from '../../pricing.mapper'
import { PricingTableModel } from '../../pricing.Model'
import { fetchPriceDetails, savePrice } from '../../pricing.service'
import { AddDefaultValueDialog } from './components/addDefaultValueDialog'
import { DummyPriceEditDialog } from './components/dummyPriceEditDialog'
import { PcTypeInstancesHeaderActions } from './components/pcTypeInstancesHeaderActions'
import { PriceComponentInstancesSearchCard } from './components/priceComponentInstancesSearchCard/priceComponentInstancesSearchCard'
import { PriceComponentTypeLeftPanelDetails } from './components/priceComponentTypeDetails'
import { PriceComponentTypeInstacesDataTable } from './components/priceComponentTypeInstancesDataTable'
import {
  useGetPriceComponentByPcTypeID,
  useGetPriceComponentDefaultValue,
  useSearchPriceComponentInstances
} from './hooks/priceComponentType.instances.hooks'
import { PriceComponentInstancesSearchFilter } from './models/priceComponentInstancesSearch.model'

interface PriceComponentTypeInstancesPageProps {
  location?: {
    state: {
      pricingTableRowSelected?: PricingTableModel
      commodity: EnumCommodity
      exceedsMultipleInstances?: boolean
    }
  }
}

export const PriceComponentTypeInstancesPage: React.FC<
  PriceComponentTypeInstancesPageProps
> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'priceComponentTypeInstances'])
  const history = useHistory()
  const { info, error } = useNotify()
  const [showPricePeriod, setShowPricePeriod] = useState<boolean>(false)
  const pricePeriodCardRef = useRef<HTMLDivElement>(null)
  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [openDummyPriceEdit, setOpenDummyPriceEdit] = useState<boolean>(false)
  const [showAddDefaultValueDialog, setShowAddDefaultValueDialog] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [formInputData, setFormInputData] = useState<any>({})
  const [dummyPriceExpression, setDummyPriceExpression] = useState<string>('')
  const [priceEditableModelFields, setPriceEditableModelFields] =
    useState<EditablePriceModel | null>(null)
  const [isFetchingPriceExpression, setIsFetchingPriceExpression] = useState<boolean>(false)
  const [showSearchCard, setShowSearchCard] = useState<boolean>(false)
  const [searchFilters, setSearchFilters] = useState<
    PriceComponentInstancesSearchFilter | undefined
  >(undefined)

  const pricingTableRowSelected = props.location?.state.pricingTableRowSelected
  const commodity = props.location?.state.commodity
  const exceedsMultipleInstances = props.location?.state.exceedsMultipleInstances ?? false
  const companyId = getActiveCompany()?.id
  if (!pricingTableRowSelected) {
    history.goBack()
    return null
  }

  // Usar hook apropiado según la cantidad de instancias
  const {
    isFetching: isFetchingPriceComponentData,
    fetchData: priceComponentsData,
    getPriceComponentByPcTypeID: refreshPriceComponents
  } = useGetPriceComponentByPcTypeID(
    (errorMsg: string) => error(t(errorMsg)),
    pricingTableRowSelected.idPriceComponentType!,
    commodity ?? 1,
    companyId ?? 1,
    !exceedsMultipleInstances // Solo fetch automático si NO excede el threshold
  )

  const {
    isFetching: isFetchingDefaultValue,
    fetchData: defaultValue,
    getPriceComponentDefaultValue: refreshDefaultValue
  } = useGetPriceComponentDefaultValue(
    (errorMsg: string) => error(t(errorMsg)),
    pricingTableRowSelected.idPriceComponentType!,
    pricingTableRowSelected.priceComponentTypeReference ?? EnumPriceComponentReference.General,
    commodity ?? 1,
    companyId ?? 1,
    exceedsMultipleInstances // Solo fetch si excede el threshold
  )

  const {
    isFetching: isFetchingSearch,
    fetchData: searchResults,
    searchPriceComponentInstances
  } = useSearchPriceComponentInstances((errorMsg: string) => error(t(errorMsg)))

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const codeString = pricingTableRowSelected.priceComponentTypeCode ?? ''
  const descriptionString = pricingTableRowSelected.priceComponentTypeDescription ?? ''
  const priceTypeString =
    enumEnumToValueString(EnumPriceType, pricingTableRowSelected.priceType) ?? ''
  const referenceTypeString =
    enumEnumToValueString(
      EnumPriceComponentReference,
      pricingTableRowSelected.priceComponentTypeReference
    ) ?? ''
  const priceType = enumEnumToValueNumber(EnumPriceType, pricingTableRowSelected.priceType) ?? 1
  const referenceType =
    enumEnumToValueNumber(
      EnumPriceComponentReference,
      pricingTableRowSelected.priceComponentTypeReference
    ) ?? 1
  const commodityString =
    enumEnumToValueString(EnumCommodity, commodity ?? pricingTableRowSelected.commodity) ?? ''

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleEditDummyPrice = () => {
    setOpenDummyPriceEdit(true)
    pricingTableRowSelected.idDummyPrice && getPriceExpression(pricingTableRowSelected.idDummyPrice)
  }

  const handleSave = async () => {
    // Save Logic
  }

  const handleAddDefaultValue = () => {
    setShowAddDefaultValueDialog(true)
  }

  const handleAddDefaultValueClose = (refreshNeeded?: boolean) => {
    setShowAddDefaultValueDialog(false)
    if (refreshNeeded) {
      exceedsMultipleInstances ? refreshDefaultValue() : refreshPriceComponents()
    }
  }

  // ------------------- DataTable Action Handlers -----------------------------//

  const handlePriceComponentAdd = (priceType: EnumPriceType) => {
    history.push(NonAuthRoutes.PriceComponentsAdd, {
      id: null,
      priceType: priceType,
      commodity: commodity,
      priceComponentTypeReference: referenceType,
      formMode: FormDialogMode.ADD,
      addInstanceFromPriceComponentType: true,
      idDummyPrice: pricingTableRowSelected.idDummyPrice,
      priceComponentTypeID: pricingTableRowSelected.idPriceComponentType,
      instancesToFilter: filterInstances(priceComponentsData) // to filter already used instances in the selectOptions of lead/peaje/presionTension
    })
  }

  const handlePriceComponentEdit = (selectedRow: PriceComponentInstancesDataTableModel) => {
    history.push(NonAuthRoutes.PriceComponentsAdd, {
      id: selectedRow.id,
      priceComponentTypeID: pricingTableRowSelected.idPriceComponentType,
      priceType: enumEnumToValueNumber(EnumPriceType, selectedRow.price.priceType),
      commodity: commodity,
      priceComponentTypeReference: referenceType,
      tollID: enumEnumToValueNumber(EnumTipoPeaje, selectedRow.toll),
      presionTensionID: enumEnumToValueNumber(EnumPresionTension, selectedRow.presionTension),
      leadID: selectedRow.lead?.id,
      regionImpuestosID: selectedRow.regionImpuestos,
      formMode: FormDialogMode.EDIT,
      parametroPeriodoPriceID:
        enumEnumToValueNumber(EnumPriceType, selectedRow.price.priceType) ===
        EnumPriceType.ParametroPeriodo
          ? selectedRow.price?.id
          : null
    })
  }

  const handleAdvancedPriceEdit = (selectedRow: PriceComponentInstancesDataTableModel) => {
    history.push(NonAuthRoutes.pricingEdit, {
      priceId: selectedRow.price.id,
      priceType: enumEnumToValueNumber(EnumPriceType, selectedRow.price.priceType),
      commodity: enumEnumToValueNumber(EnumCommodity, selectedRow.priceComponentType.commodity),
      priceComponentId: selectedRow.id
    })
  }

  const handlePriceComponentDelete = async (selectedRow: PriceComponentInstancesDataTableModel) => {
    await getConfirmation(
      selectedRow.isDefaultValue
        ? ConfirmationDialogs.DELETE_PRICECOMPONENT_DEFAULT_VALUE
        : ConfirmationDialogs.DELETE_PRICECOMPONENT
    ).then(async confirm => {
      await deletePriceComponent(selectedRow.id!).then(response => {
        if (response.success) {
          refreshPriceComponents()
          info(t('priceComponentTypeInstances:notifications.deletePriceComponent.success'))
        } else {
          error(t('priceComponentTypeInstances:notifications.deletePriceComponent.error'))
        }
      })
    })
    closeConfirmation()
  }

  // ------------------- Dummy Price Edit Dialog Handlers -----------------------------//
  const handleCloseDummyPriceEdit = (priceFields?: EditablePriceModel) => {
    if (priceFields) {
      updateDummyPrice(priceFields)
    }
    setOpenDummyPriceEdit(false)
  }

  // ------------------- Search Card Handlers -----------------------------//
  const handleSearch = (filters: PriceComponentInstancesSearchFilter) => {
    setSearchFilters(filters)
    searchPriceComponentInstances(
      filters.priceComponentTypeId,
      filters.commodity,
      filters.companyId,
      filters.referenceType,
      filters.searchText,
      filters.leadIds,
      filters.tollIds,
      filters.presionTensionIds,
      filters.regionImpuestosIds
    )
  }

  const handleCleanFilters = () => {
    setSearchFilters(undefined)
    setShowSearchCard(false)
    // Recargar datos sin filtros
    if (exceedsMultipleInstances) {
      // No hacer nada, esperar nueva búsqueda
    } else {
      refreshPriceComponents()
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const updateDummyPrice = async (priceFields: EditablePriceModel) => {
    await savePrice(priceFields.id!, priceFields).then(response => {
      if (response.success) {
        info(t('priceComponentTypeInstances:notifications.dummyPriceEdit.success'))
        refreshPriceComponents()
      } else {
        error(t('priceComponentTypeInstances:notifications.dummyPriceEdit.error'))
      }
    })
  }

  const getPriceExpression = async (dummyPriceId: number) => {
    setIsFetchingPriceExpression(true)
    await fetchPriceDetails(dummyPriceId)
      .then(response => {
        if (response.success && response.data) {
          setPriceEditableModelFields(mapEditableModelPriceFields(response.data))
        } else {
          error(t('priceComponentTypeInstances:notifications.fetchPriceExpression.error'))
        }
      })
      .finally(() => {
        setIsFetchingPriceExpression(false)
      })
  }

  const filterInstances = (data: PriceComponentInstancesDataTableModel[] | undefined) => {
    let filteredInstances: number[] = []
    if (data && data.length > 0) {
      filteredInstances = data.map(priceComponentInstance => {
        switch (referenceType) {
          case EnumPriceComponentReference.PresionTension:
            return priceComponentInstance.presionTension ?? 0
          case EnumPriceComponentReference.Lead:
            return priceComponentInstance.lead?.id ?? 0
          case EnumPriceComponentReference.Peaje:
            return priceComponentInstance.toll ?? 0
          case EnumPriceComponentReference.RegionImpuestos:
            return priceComponentInstance.regionImpuestos?.id ?? 0
          default:
            return 0
        }
      })
    }
    return filteredInstances
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts

  // Separar valor por defecto y otras instancias
  const { defaultValueData, otherInstancesData } = useMemo(() => {
    if (exceedsMultipleInstances) {
      // Cuando excede, defaultValue viene del hook separado
      // Mostrar SearchCard automáticamente
      setShowSearchCard(true)
      return {
        defaultValueData: defaultValue,
        otherInstancesData: searchFilters ? searchResults : [] // Mostrar resultados de búsqueda si hay filtros
      }
    } else {
      // Cuando NO excede, separamos del array completo
      const defaultVal = priceComponentsData?.find(pc => pc.isDefaultValue === true)
      const others = priceComponentsData?.filter(pc => pc.isDefaultValue !== true) || []
      return {
        defaultValueData: defaultVal,
        otherInstancesData: others
      }
    }
  }, [exceedsMultipleInstances, defaultValue, priceComponentsData, searchFilters, searchResults])

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN}>
      <PageLeftColumn>
        <PriceComponentTypeLeftPanelDetails
          code={codeString}
          description={descriptionString}
          priceType={priceTypeString}
          referenceType={referenceTypeString}
          commodity={commodityString}
        />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('priceComponentTypeInstances:header.title')}>
          <PcTypeInstancesHeaderActions
            onBackButtonClick={handleBackButtonClick}
            onEditDummyPriceClick={handleEditDummyPrice}
            onSaveClick={handleSave}
            referenceType={referenceType as EnumPriceComponentReference}
          />
        </PageHeader>

        {/* Tabla de Valor por Defecto */}
        <PriceComponentTypeInstacesDataTable
          isFetching={
            exceedsMultipleInstances ? isFetchingDefaultValue : isFetchingPriceComponentData
          }
          referenceType={referenceType as EnumPriceComponentReference}
          componentPriceType={priceType}
          priceComponentsData={defaultValueData ? [defaultValueData] : []}
          isDefaultValueTable={true}
          title={t('priceComponentTypeInstances:dataTable.title.defaultValue')}
          onAddDefaultValueActionHandler={handleAddDefaultValue}
          onPriceComponentAddActionHandler={handlePriceComponentAdd}
          onPriceComponentEditActionHandler={handlePriceComponentEdit}
          onAdvancedPriceEditActionHandler={handleAdvancedPriceEdit}
          onPriceComponentDeleteActionHandler={handlePriceComponentDelete}
        />

        {/* Card de Búsqueda - Solo visible cuando exceedsMultipleInstances */}
        {exceedsMultipleInstances && (
          <PriceComponentInstancesSearchCard
            showCard={showSearchCard}
            referenceType={referenceType as EnumPriceComponentReference}
            commodity={commodity ?? 1}
            priceComponentTypeId={pricingTableRowSelected.idPriceComponentType!}
            companyId={companyId ?? 1}
            onSearch={handleSearch}
            onCleanFilters={handleCleanFilters}
          />
        )}

        {/* Tabla de Otras Instancias */}
        <PriceComponentTypeInstacesDataTable
          isFetching={exceedsMultipleInstances ? isFetchingSearch : isFetchingPriceComponentData}
          referenceType={referenceType as EnumPriceComponentReference}
          componentPriceType={priceType}
          priceComponentsData={otherInstancesData}
          isDefaultValueTable={false}
          title={t('priceComponentTypeInstances:dataTable.title.default')}
          onPriceComponentAddActionHandler={handlePriceComponentAdd}
          onPriceComponentEditActionHandler={handlePriceComponentEdit}
          onAdvancedPriceEditActionHandler={handleAdvancedPriceEdit}
          onPriceComponentDeleteActionHandler={handlePriceComponentDelete}
        />

        <DummyPriceEditDialog
          open={openDummyPriceEdit}
          onClose={handleCloseDummyPriceEdit}
          priceType={priceType}
          commodity={commodity!}
          dummyPriceData={{ ...priceEditableModelFields }}
          // this parameters is needed to get the expression transformed variables instead of referencials ID to operators/variables/parameters
          priceExpression={dummyPriceExpression}
          isFetchingPriceExpression={isFetchingPriceExpression}
        />
        {showAddDefaultValueDialog && (
          <AddDefaultValueDialog
            open={showAddDefaultValueDialog}
            onClose={handleAddDefaultValueClose}
            pricingTableRowSelected={pricingTableRowSelected}
            commodity={commodity ?? 1}
          />
        )}
      </PageCenterColumn>
    </Page>
  )
}
